
import Page from '@root/common/base/Page';
import Meta from '@root/common/utils/Meta';
import { QueryResult } from '@root/modules/headlines/types/headlines';
import UniversalHeadlinesService from '@root/modules/headlines/services/UniversalHeadlines.service';
import HeadlineList from '@root/common/components/base/headline/HeadlineList.vue';
import PageHeader from '@root/common/components/base/PageHeader.vue';
import BookmarksPageSkeletonLoader from '@root/modules/customer/components/BookmarksPageSkeletonLoader.vue';
import ErrorPage from '@root/modules/customer/components/ErrorPage.vue';
import PianoCustomerStateHandler from '@piano/components/PianoCustomerStateHandler.vue';
import { GetCustomerData } from '@root/modules/customer/types/customer';
import CustomerDataService from '@root/modules/customer/services/CustomerData.service';
import CustomerNavigation from '@root/modules/customer/components/CustomerNavigation.vue';
import getPageAdsSettings from '@root/modules/customer/utils/getPageAdsSettings';

interface Filter {
  key: string;
  headlinesKey: string[];
}

const filters: Filter[] = [
  {
    key: 'all',
    headlinesKey: ['bookmark', 'video-bookmark', 'podcast-bookmark'],
  },
  {
    key: 'article',
    headlinesKey: ['bookmark'],
  },
  {
    key: 'video',
    headlinesKey: ['video-bookmark'],
  },
  {
    key: 'podcast',
    headlinesKey: ['podcast-bookmark'],
  },
];

export default Page.extend({
  components: {
    HeadlineList,
    PageHeader,
    BookmarksPageSkeletonLoader,
    ErrorPage,
    PianoCustomerStateHandler,
    CustomerNavigation,
  },
  data() {
    return {
      isLoading: true,
      bookmarks: null as GetCustomerData['data']['data'] | null,
      headlines: null as QueryResult | null,
      headlineSettings: { icons: false, contentSeparator: true },
      customerDataService: null as CustomerDataService | null,
      pager: {
        limit: 50,
        offset: 0,
        count: 0,
      },
      updateKey: Date.now(),
      filters,
      activeFilter: filters[0],
    };
  },
  computed: {
    customerToken(): string {
      return this.$store.state.piano.token;
    },
    page(): string | (string | null)[] {
      return this.$route.query.page;
    },
  },
  methods: {
    async getPageData() {
      const page = this.normalizePageNumber();
      const limit = this.pager.limit;
      const offset = (page - 1) * limit;

      await this.getBookmarks(limit, offset);
      await this.getHeadlines();
    },
    normalizePageNumber(): number {
      const queryPage = this.page;
      let page = Number(queryPage);

      if (!page || page < 1) {
        page = 1;
        window.history.replaceState(null, '', `${window.location.pathname}?page=${page}`);
      }

      return page;
    },
    async getBookmarks(limit?: number, offset?: number) {
      const response =
        (await this.customerDataService?.get({
          key: this.activeFilter.headlinesKey,
          limit: limit ?? this.pager.limit,
          offset: offset ?? this.pager.offset,
        })) || null;

      if (response?.data.items.length) {
        response.data.items = response.data.items.filter((item) => Number(item.value));
      }

      this.bookmarks = response?.data || null;
    },
    async getHeadlines() {
      if (!this.bookmarks?.items.length) {
        return;
      }

      const universalHeadlinesService = new UniversalHeadlinesService();
      const headlines = await universalHeadlinesService.fetch({
        id: this.bookmarks.items.map((item) => Number(item.value)),
        offset: this.pager.offset,
        limit: this.pager.limit,
        getCount: true,
      });

      this.headlines = headlines;
      this.updateKey = Date.now();
    },
    deleteBookmark(id: number) {
      const bookmarkIndex = this.bookmarks?.items.findIndex((bookmark) => bookmark.value === String(id));

      if (bookmarkIndex !== undefined && bookmarkIndex >= 0) {
        const bookmark = this.bookmarks!.items.splice(bookmarkIndex as number, 1)[0];
        this.getHeadlines();

        this.customerDataService?.delete({
          id: bookmark!.id,
        });
      }
    },
  },
  watch: {
    async customerToken() {
      this.customerDataService?.setCustomerToken(this.customerToken);
      this.headlines = null;
      this.bookmarks = null;

      if (this.customerToken) {
        await this.getPageData();
        this.isLoading = false;
      }
    },
    async page() {
      this.isLoading = true;
      this.headlines = null;
      this.bookmarks = null;

      await this.getPageData();

      this.isLoading = false;
    },
    async activeFilter(newValue) {
      if (this.page && Number(this.page) > 1) {
        this.$router.replace({ query: { ...this.$route.query, page: '1' } });
      }
      if (!newValue.headlines) {
        this.isLoading = true;
        this.headlines = null;
        this.bookmarks = null;

        await this.getPageData();

        this.isLoading = false;
      }
    },
  },
  async created() {
    this.setPageAdsSettings(getPageAdsSettings({ $channelConfig: this.$channelConfig }));

    this.customerDataService = new CustomerDataService();

    if (this.customerToken) {
      this.customerDataService.setCustomerToken(this.customerToken);
      await this.getPageData();
      this.isLoading = false;
    }
  },
  mounted() {
    this.restoreScroll();
    this.$eventHandler.on('delete-bookmark', this.deleteBookmark);
  },
  beforeRouteLeave(to, from, next) {
    this.resetPageAdsSettings();
    this.resetPageHeaderBottomNavigation();

    next();
  },
  beforeDestroy() {
    this.$eventHandler.off('delete-bookmark', this.deleteBookmark);
  },
  head() {
    const meta = new Meta(this.$channelConfig('meta').base, this.$route.path);

    meta.title = meta.ogTitle = this.$t('bookmark.meta.title') as string;
    meta.description = meta.ogDescription = this.$t('bookmark.meta.description') as string;

    return {
      title: meta.title,
      meta: meta.data,
      link: meta.link,
    };
  },
});
